// Copyright (c) Bentley Systems
import { useCallback, useEffect, useRef, useState } from "react";
import { AppDispatch } from "../app/store";
import { ConfigManager } from "../Helpers/config-manager";
import { setErrorState } from "../Helpers/helper-functions";
import { ClientTelemetryOverview, DayFilterValue, OnPremProjectKey, ProjectRunStatus } from "./Models/on-prem-telemetry-models";

type UseOnPremTelemetryOverviewApiProps = {
  accessToken: string;
  dispatch: AppDispatch;
  isNodeAllowed: boolean;
  /** Only runs last run within this many days are fetched; 0 (default) fetches every retained run. */
  dayFilter?: DayFilterValue;
};

/** Shape of one project-run row as returned by the `overview` endpoint (camelCase wire contract). */
type OverviewProjectRunResponse = {
  projectId: string;
  projectName?: string;
  iTwinId: string;
  lastRunSuccess?: boolean;
  lastRun: string;
  isMuted?: boolean;
};

/** Shape of one client row as returned by the `overview` endpoint (camelCase wire contract). */
type OverviewClientResponse = {
  mqttClientName: string;
  installedVersion?: string;
  lastUsed?: string;
  trackedProjectCount?: number;
  projects?: OverviewProjectRunResponse[];
};

/** Shape of one registered on-prem client as returned by QTS's `itwins/{iTwinId}/onpremises` endpoint. */
type RawOnpremiseClient = {
  /** The MQTT client ID — matches the overview's `mqttClientName`. (`name` is only the friendly name.) */
  mqttClientId: string;
  notificationEmail?: string | null;
  notes?: string | null;
};

/** Distinct non-blank values joined for display; undefined when there are none. */
const joinDistinct = (values: (string | null | undefined)[], separator: string): string | undefined => {
  const distinct = Array.from(new Set(values.map((v) => v?.trim()).filter((v): v is string => !!v)));
  return distinct.length > 0 ? distinct.join(separator) : undefined;
};

/**
 * Fills email/notes on each client once every one of its iTwins was fetched or failed. Failed iTwins
 * mark the client unavailable so the UI can say so, and are fetched again on the next attempt.
 */
const withRegistrations = (
  clients: ClientTelemetryOverview[],
  registrationsByITwin: Map<string, Map<string, RawOnpremiseClient>>,
  failedITwins: Set<string>,
): ClientTelemetryOverview[] =>
  clients.map((client) => {
    const iTwinIds = Array.from(new Set(client.projects.map((p) => p.iTwinId)));
    if (!iTwinIds.every((iTwinId) => registrationsByITwin.has(iTwinId) || failedITwins.has(iTwinId))) {
      return client;
    }
    // Matched by MQTT client ID per iTwin, since one client can be registered in several iTwins.
    const registrations = iTwinIds
      .map((iTwinId) => registrationsByITwin.get(iTwinId)?.get(client.mqttClientName))
      .filter((r): r is RawOnpremiseClient => r !== undefined);
    return {
      ...client,
      notificationEmail: joinDistinct(registrations.map((r) => r.notificationEmail), ", "),
      notes: joinDistinct(registrations.map((r) => r.notes), " | "),
      isRegistrationLoaded: true,
      isRegistrationUnavailable: iTwinIds.some((iTwinId) => failedITwins.has(iTwinId)),
    };
  });

export const useOnPremTelemetryOverviewApi = ({
  accessToken,
  dispatch,
  isNodeAllowed,
  dayFilter = 0,
}: UseOnPremTelemetryOverviewApiProps) => {
  const [clients, setClients] = useState<ClientTelemetryOverview[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isMutating, setIsMutating] = useState(false);
  // iTwinId -> mqttClientId -> registration, kept across overview refetches. Holds successful fetches only.
  const registrationsByITwin = useRef(new Map<string, Map<string, RawOnpremiseClient>>());
  // iTwins whose last fetch failed, e.g. 403 without access to that iTwin.
  const failedITwins = useRef(new Set<string>());
  const pendingITwinFetches = useRef(new Map<string, Promise<void>>());
  const registrationsAbort = useRef<AbortController | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    registrationsAbort.current = controller;
    return () => controller.abort();
  }, []);

  const mapClients = (payload: unknown): ClientTelemetryOverview[] => {
    const rows: OverviewClientResponse[] = Array.isArray(payload) ? payload : [];

    return rows.map((row) => ({
      mqttClientName: row.mqttClientName,
      installedVersion: row.installedVersion,
      lastUsed: row.lastUsed,
      trackedProjectCount: row.trackedProjectCount,
      projects: (row.projects ?? []).map(
        (p): ProjectRunStatus => ({
          projectId: p.projectId,
          projectName: p.projectName,
          iTwinId: p.iTwinId,
          lastRunSuccess: p.lastRunSuccess ?? false,
          lastRun: p.lastRun,
          isMuted: p.isMuted ?? false,
        }),
      ),
    }));
  };

  const fetchITwinRegistrations = useCallback(
    async (iTwinId: string): Promise<void> => {
      const pending = pendingITwinFetches.current.get(iTwinId);
      if (pending) {
        return pending;
      }
      const request = (async () => {
        const signal = registrationsAbort.current?.signal;
        try {
          const res = await fetch(`${ConfigManager.edfsItwinInternalUrl}/itwins/${encodeURIComponent(iTwinId)}/onpremises`, {
            headers: {
              Authorization: accessToken,
              RunId: crypto.randomUUID(),
            },
            signal,
          });
          if (!res.ok) {
            failedITwins.current.add(iTwinId);
            return;
          }
          const json = (await res.json()) as { onPremises?: RawOnpremiseClient[] };
          const byClientId = new Map<string, RawOnpremiseClient>();
          for (const onPremise of json.onPremises ?? []) {
            byClientId.set(onPremise.mqttClientId, onPremise);
          }
          registrationsByITwin.current.set(iTwinId, byClientId);
          failedITwins.current.delete(iTwinId);
        } catch {
          // Email and notes are supplementary; the telemetry still renders without them.
          if (!signal?.aborted) {
            failedITwins.current.add(iTwinId);
          }
        } finally {
          pendingITwinFetches.current.delete(iTwinId);
        }
      })();
      pendingITwinFetches.current.set(iTwinId, request);
      return request;
    },
    [accessToken],
  );

  /**
   * Loads NotificationEmail/Notes from a client's registrations (CredentialsIdentity, via QTS). Each iTwin
   * is fetched once per page visit; iTwins that failed are fetched again on the next call.
   */
  const loadClientRegistrations = useCallback(
    async (client: ClientTelemetryOverview) => {
      if (client.isRegistrationLoaded && !client.isRegistrationUnavailable) {
        return;
      }
      const missingITwinIds = Array.from(new Set(client.projects.map((p) => p.iTwinId))).filter(
        (iTwinId) => !registrationsByITwin.current.has(iTwinId),
      );
      await Promise.all(missingITwinIds.map(fetchITwinRegistrations));
      if (registrationsAbort.current?.signal.aborted) {
        return;
      }
      setClients((current) => withRegistrations(current, registrationsByITwin.current, failedITwins.current));
    },
    [fetchITwinRegistrations],
  );

  const fetchOverview = useCallback(
    async (signal?: AbortSignal) => {
      setIsLoading(true);
      const runId = crypto.randomUUID();
      const url = `${ConfigManager.projectProviderUrl}/api/OnPremTelemetry/overview?days=${dayFilter}`;

      try {
        const response = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: accessToken,
            RunId: runId,
          },
          signal,
        });

        if (!response.ok) {
          setErrorState(response.status, dispatch, runId, false);
          return;
        }

        const data = await response.json();
        setClients(withRegistrations(mapClients(data), registrationsByITwin.current, failedITwins.current));
      } catch (error: unknown) {
        if ((error as { name?: string })?.name !== "AbortError") {
          setErrorState(500, dispatch, runId, true);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [accessToken, dispatch, dayFilter],
  );

  const setProjectsMuted = useCallback(
    async (keys: OnPremProjectKey[], muted: boolean): Promise<boolean> => {
      if (keys.length === 0) {
        return false;
      }
      setIsMutating(true);
      const runId = crypto.randomUUID();
      const url = `${ConfigManager.projectProviderUrl}/api/OnPremTelemetry/projects/mute`;

      try {
        const response = await fetch(url, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: accessToken,
            RunId: runId,
          },
          body: JSON.stringify({ keys, muted }),
        });

        if (!response.ok) {
          setErrorState(response.status, dispatch, runId, false);
          return false;
        }

        await fetchOverview();
        return true;
      } catch {
        setErrorState(500, dispatch, runId, true);
        return false;
      } finally {
        setIsMutating(false);
      }
    },
    [accessToken, dispatch, fetchOverview],
  );

  const deleteProjectRuns = useCallback(
    async (keys: OnPremProjectKey[]): Promise<boolean> => {
      if (keys.length === 0) {
        return false;
      }
      setIsMutating(true);
      const runId = crypto.randomUUID();
      const url = `${ConfigManager.projectProviderUrl}/api/OnPremTelemetry/projects/delete`;

      try {
        const response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: accessToken,
            RunId: runId,
          },
          body: JSON.stringify(keys),
        });

        if (!response.ok) {
          setErrorState(response.status, dispatch, runId, false);
          return false;
        }

        await fetchOverview();
        return true;
      } catch {
        setErrorState(500, dispatch, runId, true);
        return false;
      } finally {
        setIsMutating(false);
      }
    },
    [accessToken, dispatch, fetchOverview],
  );

  useEffect(() => {
    if (!isNodeAllowed) {
      return;
    }
    const abortController = new AbortController();
    void fetchOverview(abortController.signal);
    return () => {
      abortController.abort();
    };
  }, [fetchOverview, isNodeAllowed]);

  return {
    clients,
    isLoading,
    isMutating,
    fetchOverview,
    loadClientRegistrations,
    setProjectsMuted,
    deleteProjectRuns,
  };
};
